import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppContext } from './Core';
import { useRecipientFilter } from './RecipientFilter';
import { useMessageDetailsForm } from './MessageDetailsForm';
import { downloadRecipientsAPI, getFilteredRecipients, getFiltersListForTeam } from '../APIs/RecipientFilterAPI';
import { ACTIONS } from '../consts/CommonV2';
import { resolve } from 'path-browserify';
import { SNACK_TYPES } from '../consts/Common';
import { TEAM } from '../consts/DBFields';
import { formatDate } from '../utils/common';

// Create and export the context
export const SavedFiltersManagementContext = createContext();
export const SavedFiltersManagementState = SavedFiltersManagementContext.Consumer;
export const useSavedFiltersManagement = () => useContext(SavedFiltersManagementContext);

const SavedFiltersManagementProvider = ({ children }) => {
  const { setSnackMessage, setSnackType, selectedTeam } = useAppContext();
  const history = useHistory();
  const { setRawFilter, setFilterId, setFilterName, setParsedFilter, setSharedWith, sharedWith, resetFilter, parseFilter, fetchRecipientCount } = useRecipientFilter();
  const { setShowRecipientFilterOverlay } = useMessageDetailsForm();

  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFiltersError, setSavedFiltersError] = useState(null);

  // State for managing division list/recipient list overlay
  const [showDivisionOverlay, setShowDivisionOverlay] = useState(false);
  const [selectedFilterForDivision, setSelectedFilterForDivision] = useState(null);
  const [divisionListData, setDivisionListData] = useState([]);

  // State for managing filter operations and pagination
  const [divisionListLoading, setDivisionListLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simplified caching and pagination state
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [hasMoreData, setHasMoreData] = useState(true);
  const [lastSearchAfter, setLastSearchAfter] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [apiTotalCount, setApiTotalCount] = useState(0);
  const [currentFilterId, setCurrentFilterId] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [mode, setMode] = useState(ACTIONS.RECIPIENT_PAGE_SAVED_FILTERS_VIEW); // 'create' or 'update' mode when creating/updating filter

  // Refs to track current API calls and prevent duplicate requests
  const currentApiCallRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Constants for pagination
  const PAGE_SIZE = 100;
  const MAX_ITEMS = 500;

  // Transform API response data to the expected format for the UI
  const transformRecipientData = (response) => {
    if (!response || !response?.receipientList) {
      return [];
    }

    return response?.receipientList.map((item) => ({
      name: item?.name || 'Unknown Name',
      email: item?.email || 'Unknown Email',
    }));
  };

  // Function to fetch recipients with simplified pagination
  const fetchRecipientsForFilter = useCallback(async (filter, searchTerm = '', isLoadMore = false, searchAfter = null) => {
    if (!filter?.filterId) return;

    console.log('fetchRecipientsForFilter called:', {
      filter: filter,
      filterId: filter?.filterId,
      searchTerm,
      isLoadMore,
      hasSearchAfter: !!searchAfter
    });

    // Cancel any previous API call
    if (currentApiCallRef.current) {
      currentApiCallRef.current.abort();
    }

    // Set appropriate loading states
    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setDivisionListLoading(true);
    }

    // If switching filters or search terms, reset data
    const isNewRequest = !isLoadMore || currentFilterId !== filter?.filterId || currentSearchTerm !== searchTerm;
    if (isNewRequest && !isLoadMore) {
      setDivisionListData([]);
      setCurrentFilterId(filter?.filterId);
      setCurrentSearchTerm(searchTerm);
      setLastSearchAfter(null);
      setHasMoreData(true);
      setTotalCount(0);
    }

    setError(null);

    try {
      // Create AbortController for this API call
      const abortController = new AbortController();
      currentApiCallRef.current = abortController;

      // Prepare API payload
      const payload = {
        size: PAGE_SIZE.toString(),
        filterId: filter?.filterId,
        searchTerm: searchTerm.trim()
      };

      // Add searchAfter for pagination (load more)
      if (searchAfter) {
        payload.searchAfter = searchAfter;
      }

      const response = await getFilteredRecipients(payload);
      const countResponse = await fetchRecipientCount(filter?.filter)

      // Debug logging
      console.log('API Response:', response);
      console.log('Payload sent:', payload);
      console.log('Count Response:', countResponse);

      // Check if request was aborted
      if (abortController.signal.aborted) return;

      // Process successful response
      const transformedData = transformRecipientData(response);
      const newLastSearchAfter = response?.searchAfterKey || null;
      const apiTotalCount = countResponse?.count || 0;

      // Calculate actual total items (max 500)
      const actualTotalCount = Math.min(apiTotalCount, MAX_ITEMS);
      setTotalCount(actualTotalCount);
      setApiTotalCount(apiTotalCount)

      if (isLoadMore) {
        // Append new data to existing data
        setDivisionListData(prevData => {
          const updatedData = [...prevData, ...transformedData];

          // Check if we've reached the max limit or no more data
          const hasMore = updatedData.length < actualTotalCount &&
            updatedData.length < MAX_ITEMS &&
            newLastSearchAfter;

          setHasMoreData(hasMore);
          setLastSearchAfter(newLastSearchAfter);

          return updatedData;
        });
      } else {
        // Replace existing data (initial load or new search)
        setDivisionListData(transformedData);

        // Check if we have more data to load
        const hasMore = transformedData.length < actualTotalCount &&
          transformedData.length < MAX_ITEMS &&
          newLastSearchAfter;

        setHasMoreData(hasMore);
        setLastSearchAfter(newLastSearchAfter);
      }

      console.log('Data loaded:', {
        newItems: transformedData.length,
        totalInUI: isLoadMore ? 'will be calculated' : transformedData.length,
        apiTotalCount,
        actualTotalCount,
        hasMoreData: isLoadMore ? 'will be calculated' : transformedData.length < actualTotalCount && transformedData.length < MAX_ITEMS && newLastSearchAfter,
        searchAfterKey: newLastSearchAfter,
        isSearch: searchTerm.trim() !== '',
        currentDataLength: divisionListData.length
      });

    } catch (err) {
      // Don't show error if request was aborted
      if (err.name === 'AbortError') return;

      console.error('Error fetching recipients:', err);
      console.error('Filter:', filter);
      console.error('Search term:', searchTerm);
      console.error('Is load more:', isLoadMore);

      const errorMessage = err.message || 'Failed to load recipient data';
      setError(errorMessage);
      setSnackMessage(`Failed to load recipients: ${errorMessage}`);
      setSnackType('error');
    } finally {
      setDivisionListLoading(false);
      setIsLoadingMore(false);
      currentApiCallRef.current = null;
    }
  }, [currentFilterId, currentSearchTerm, setSnackMessage, setSnackType]);

  // Function to handle search with debouncing
  const searchRecipients = useCallback((filter, searchTerm) => {
    console.log('searchRecipients called:', { filterId: filter?.filterId, searchTerm });

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set debounced search
    searchDebounceRef.current = setTimeout(() => {
      console.log('Executing debounced search for:', searchTerm);
      // Reset pagination state for new search
      setCurrentSearchTerm(searchTerm);
      fetchRecipientsForFilter(filter, searchTerm, false);
    }, 500);
  }, [fetchRecipientsForFilter]);

  // Function to load more recipients
  const loadMoreRecipients = useCallback((filter, searchTerm = '') => {
    console.log('loadMoreRecipients called:', {
      filterId: filter?.filterId,
      searchTerm,
      hasMoreData,
      hasSearchAfter: !!lastSearchAfter,
      isLoading: divisionListLoading,
      currentDataLength: divisionListData.length,
      totalCount,
      maxItems: MAX_ITEMS
    });

    // Check all conditions before loading more
    if (!hasMoreData ||
      !lastSearchAfter ||
      divisionListLoading ||
      divisionListData.length >= MAX_ITEMS ||
      divisionListData.length >= totalCount) {
      console.log('loadMoreRecipients: Conditions not met, skipping');
      return;
    }

    fetchRecipientsForFilter(filter, searchTerm, true, lastSearchAfter);
  }, [hasMoreData, lastSearchAfter, divisionListLoading, divisionListData.length, totalCount, fetchRecipientsForFilter]);

  // Legacy function name for backward compatibility - now uses the new API
  const fetchDivisionListForFilter = useCallback(async (filter) => {
    setCurrentSearchTerm('');
    setCurrentFilterId(filter?.filterId || null);
    await fetchRecipientsForFilter(filter, '', false);
  }, [fetchRecipientsForFilter]);

  // Handler for viewing a saved filter (shows division/recipient list)
  const handleViewRecipientFilter = useCallback((filter) => {
    setSelectedFilterForDivision(filter);
    setShowDivisionOverlay(true);
    setMode(ACTIONS.RECIPIENT_PAGE_FILTER_VIEW);
  }, []);

  // Handler for editing a saved filter
  const handleEditRecipientFilter = useCallback((filter) => {
    // if(selectedFilterForDivision) {
    setRawFilter(selectedFilterForDivision.filter);
    setFilterId(selectedFilterForDivision?.filterId);
    setFilterName(selectedFilterForDivision.name);
    setSharedWith(selectedFilterForDivision.sharedWith || []);
    setShowRecipientFilterOverlay(true);
    setMode(ACTIONS.RECIPIENT_PAGE_FILTER_EDIT);
    // }
  }, [selectedFilterForDivision]);

  // Handler for duplicating a saved filter
  const handleDuplicateRecipientFilter = (filter) => {
    console.log('Duplicating recipient filter:');
    console.log({
      ...filter,
      filterId: null,
    });
    setSelectedFilterForDivision({
      ...filter,
      filterId: null,
    });
    setFilterId(null);
    setShowRecipientFilterOverlay(true);
    setMode(ACTIONS.RECIPIENT_PAGE_FILTER_EDIT);
  };

  // Handler for downloading recipient list)
  const handleDownloadRecipientList = useCallback(async (filterId) => {
    try {
      if (!filterId) { 
        return;
      }
      // const result = await downloadRecipientsAPI(filterId);
      console.log('handle download recipient list called for filterId:', filterId);
      const payload = { filterId: filterId}
      // const result = await new Promise((resolve) => setTimeout(() => resolve({ success: true }), 4000)); // Mock API call
      const result = await downloadRecipientsAPI(payload);
    } catch (err) {
      setSnackMessage("Error processing download request");
      setSnackType(SNACK_TYPES.ERROR);
      return err;
      
    }
  }, [setSnackMessage, setSnackType]);

  // Handler for deleting a saved filter
  const handleDeleteRecipientFilter = useCallback(async (filter) => {
    console.log('Delete recipient filter clicked for:', filter.name);
    setDivisionListLoading(true);
    setError(null);

    try {
      // TODO: Implement delete filter API call
      // await deleteFilter(filter.id);

      // Mock implementation for now
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      setSnackMessage(`Successfully deleted "${filter.name}"`);
      setSnackType('success');

      // TODO: Refresh the filters list after successful deletion

    } catch (err) {
      console.error('Error deleting filter:', err);
      setError('Failed to delete the filter. Please try again.');
      setSnackMessage('Failed to delete the filter');
      setSnackType('error');
    } finally {
      setDivisionListLoading(false);
    }
  }, [setSnackMessage, setSnackType]);

  // Handler for creating a new recipient filter
  const handleCreateRecipientFilter = useCallback(() => {
    // create filter page or open create filter modal
    console.log('handle create recipient filter called')
    setMode(ACTIONS.RECIPIENT_PAGE_FILTER_CREATE)
    setShowRecipientFilterOverlay(true);
  }, []);

  // Handler for general more actions menu
  const handleMoreActions = useCallback((filter) => {
    console.log('More actions clicked for:', filter.name);
    // This is typically handled by the options menu component itself
    // but can be used for additional logging or analytics
  }, []);

  // Handler for closing division list overlay
  const handleCloseDivisionList = useCallback(() => {
    console.log('handle close division list called')
    // Cancel any ongoing API call
    if (currentApiCallRef.current) {
      currentApiCallRef.current.abort();
    }

    // Clear search debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    resetFilter();
    setShowDivisionOverlay(false);
    setShowRecipientFilterOverlay(false);
    setSelectedFilterForDivision(null);
    setDivisionListData([]);
    setMode(ACTIONS.RECIPIENT_PAGE_SAVED_FILTERS_VIEW);
    setCurrentSearchTerm('');
    setCurrentFilterId(null);
    setHasMoreData(true);
    setLastSearchAfter(null);
    setTotalCount(0);
    setApiTotalCount(0);
    setIsLoadingMore(false);
    setError(null);
  }, []);

  // Cleanup function for component unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing API call on unmount
      if (currentApiCallRef.current) {
        currentApiCallRef.current.abort();
      }

      // Clear search debounce timer on unmount
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if(selectedTeam?.[TEAM.ID]) {
      fetchRecipientFiltersListForTeam();
    }
  }, [selectedTeam?.[TEAM.ID]]);

  useEffect(() => {
    setFilterId(selectedFilterForDivision?.filterId || null);
    setFilterName(selectedFilterForDivision?.name || '');
    setRawFilter(selectedFilterForDivision?.filter || null);
    setSharedWith(selectedFilterForDivision?.sharedWith || []);
    setParsedFilter(parseFilter(selectedFilterForDivision?.filter) || null);
  }, [selectedFilterForDivision])

  const handlePrevious = useCallback(({ filterName, rawFilter, recipientCount }) => {
    setMode(prev => {
      if (prev === ACTIONS.RECIPIENT_PAGE_FILTER_EDIT) {
        setSelectedFilterForDivision( prev => {
            return {...prev, name: filterName, filter: rawFilter, recipientCount: recipientCount};          
        })
        return ACTIONS.RECIPIENT_PAGE_FILTER_VIEW;
      } else {
        setSelectedFilterForDivision(prev => {
          resetFilter();
          return null;
        });
        return ACTIONS.RECIPIENT_PAGE_SAVED_FILTERS_VIEW;
      }
    });
  }, [selectedFilterForDivision]);

  // Navigation handler for breadcrumb - Navigate back to Recipients/Saved Filters page
  const handleNavigateToSavedFilters = useCallback(async () => {
    console.log('Navigating back to Recipients page...');

    // Close the division list overlay
    handleCloseDivisionList();

    // Navigate to the Recipients page
    history.push('/recipients');

    // setSnackMessage('Navigated back to Saved Recipient Filters');
    // setSnackType('info');
  }, [handleCloseDivisionList, setSnackMessage, setSnackType]);

  // Navigation handler for selected filter breadcrumb - Stay on current page but refresh data
  const handleNavigateToSelectedFilter = useCallback(async () => {
    setSelectedFilterForDivision({ ...selectedFilterForDivision });
    setMode(ACTIONS.RECIPIENT_PAGE_FILTER_VIEW);
    setShowRecipientFilterOverlay(false);
  }, [selectedFilterForDivision]);

  const fetchRecipientFiltersListForTeam = async (forceRefresh = false) => {
    // Don't refetch if we already have data unless forced
    if (savedFilters.length > 0 && !forceRefresh) return;

    try{
      setSavedFiltersLoading(true);
      setSavedFiltersError(null);
      const teamId = selectedTeam[TEAM.ID];
      const filtersList = await getFiltersListForTeam(teamId);
      const transformedList = filtersList.map( filter => ({
        name: filter?.name,
        recipientCount: filter?.recipient_count,
        createdBy: filter?.created_by,
        sharedWith: filter?.shared_with,
        teamId: filter?.team_id,
        filterId: filter?.filter_id,
        filter: filter?.filter,
        createdDate: formatDate(filter?.date_created),
        lastUsed: filter?.last_refreshed ? formatDate(filter?.last_refreshed) : null,
      }));
      setSavedFilters(transformedList);
      return transformedList;
    } catch (error) {
      setSnackMessage("Failed to fetch recipient filter list");
      setSnackType(SNACK_TYPES.ERROR);
      setSavedFiltersError('Failed to load saved filters. Please try again.');
      return error;
    } finally {
      setSavedFiltersLoading(false);
    }
  }


  // Context value with all handlers and state
  const contextValue = {
    // Division list/overlay state
    showDivisionOverlay,
    selectedFilterForDivision,
    setSelectedFilterForDivision,
    divisionListData,
    error,

    // Pagination and search state
    currentSearchTerm,
    hasMoreData,
    totalCount,
    apiTotalCount,
    divisionListLoading,
    isLoadingMore,
    isLoading: divisionListLoading, // Legacy alias
    mode,
    setMode,
    savedFiltersLoading,

    // Action handlers
    handleViewRecipientFilter,
    handleEditRecipientFilter,
    handleDownloadRecipientList,
    handleDeleteRecipientFilter,
    handleCreateRecipientFilter,
    handleMoreActions,
    handleCloseDivisionList,
    handleDuplicateRecipientFilter,

    // Navigation handlers
    handleNavigateToSavedFilters,
    handleNavigateToSelectedFilter,
    handlePrevious,

    // API functions
    fetchRecipientsForFilter,
    searchRecipients,
    loadMoreRecipients,
    refreshSavedFilters: () => fetchRecipientFiltersListForTeam(true),

    // Legacy function (backward compatibility)
    fetchDivisionListForFilter,

    // State setters (for advanced use cases)
    setError,
    setDivisionListLoading,
    setDivisionListData,
    savedFilters,
  };

  return (
    <SavedFiltersManagementContext.Provider value={contextValue}>
      {children}
    </SavedFiltersManagementContext.Provider>
  );
};

export default SavedFiltersManagementProvider;
